"use client";

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useIMEI } from '@/lib/contexts/IMEIContext';
import { useEligibleReason } from '@/lib/contexts/EligibleReasonContext';
import { validateIMEI } from '@/lib/validation/inputValidation';
import { getBrandNameFromHostname, isSpanishURL } from '@/lib/utils/brandUtils';
import ComponentRenderer from '@/components/unlockPortal/ComponentRenderer';
import ErrorBanner from '@/components/unlockPortal/ErrorBanner';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useRecaptchaBadge } from '@/hooks/useRecaptchaBadge';
import { useRecaptchaSubmit } from '@/hooks/useRecaptchaSubmit';
import { usePageData } from '@/hooks/usePageData';
import { useMountedState } from '@/hooks/useMountedState';
import { log } from '@/lib/utils/logger';
import { preloadRecaptchaScript, getRecaptchaSiteKey } from '@/lib/utils/recaptcha';
import { FORM_CONSTANTS, MESSAGE_CONSTANTS, ROUTE_CONSTANTS, STYLE_CONSTANTS, RECAPTCHA_CONSTANTS, type StatusRoute } from '@/lib/constants/appConstants';
import { safeFetch, RecaptchaError } from '@/lib/errors/AppErrors';
import type { EligibilityResponse, ParsedDeviceUnlockStatus, EligibilityReason } from '@/lib/types/api';
import {
  getDeviceDelayedStatus,
  getDeviceEligibleStatus,
  getDeviceIneligibleStatus,
  getDeviceLockedStatus,
  getDevicePendingStatus,
  getDevicePhoneNotActivatedStatus,
  getDeviceUnlockedStatus,
} from '@/store/sagas/serverApis/deviceUnlockNew';
import { clearRequestCache, getCachedData, getCachedRequest, setCachedData, setCachedRequest } from '@/lib/utils/apiCache';

// Extend Window interface for reCAPTCHA
declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

const STATUS_API_MAP: Record<StatusRoute, (imei: string, brand: string) => Promise<unknown>> = {
  [ROUTE_CONSTANTS.STATUS.LOCKED]: getDeviceLockedStatus,
  [ROUTE_CONSTANTS.STATUS.UNLOCKED]: getDeviceUnlockedStatus,
  [ROUTE_CONSTANTS.STATUS.ELIGIBLE]: getDeviceEligibleStatus,
  [ROUTE_CONSTANTS.STATUS.INELIGIBLE]: getDeviceIneligibleStatus,
  [ROUTE_CONSTANTS.STATUS.PENDING]: getDevicePendingStatus,
  [ROUTE_CONSTANTS.STATUS.PHONE_NOT_ACTIVATED]: getDevicePhoneNotActivatedStatus,
  [ROUTE_CONSTANTS.STATUS.DELAYED]: getDeviceDelayedStatus,
};

const UnlockPortalHomePage: React.FC = () => {
  const router = useRouter();
  const { setIMEI } = useIMEI();
  const { setEligibleReason } = useEligibleReason();
  const [statusLoading, setStatusLoading] = useState(false);
  const [errorBannerMessage, setErrorBannerMessage] = useState<string | null>(null);
  
  const brand = getBrandNameFromHostname();
  // Debug logging for brand detection
  log.debug(`Detected brand from hostname: ${brand}`);
  
  const isSpanish = isSpanishURL();
  log.debug(`Is Spanish URL: ${isSpanish}`);
  const localeMessages = isSpanish ? MESSAGE_CONSTANTS.SPANISH : MESSAGE_CONSTANTS;
  
  // Flag to control reCAPTCHA usage - can be controlled via env variable or config
  const isRecaptchaEnabled = process.env.NEXT_PUBLIC_ENABLE_RECAPTCHA !== 'false';
  log.debug(`reCAPTCHA enabled: ${isRecaptchaEnabled}`);
  
  // Custom hooks for separated concerns
  const { networkSlow } = useNetworkStatus();
  const { 
    showBadgeWithLoading, 
    showBadgeReady, 
    hideBadge,
    isRecaptchaReady
  } = useRecaptchaBadge({ isSlowNetwork: networkSlow, enabled: isRecaptchaEnabled });
  const { pageData, loading: pageDataLoading } = usePageData(brand);
  useRecaptchaSubmit({ 
    networkSlow, 
    badgeControls: {
      showWithLoading: showBadgeWithLoading,
      showReady: showBadgeReady,
      hide: hideBadge
    }
  });
  const { createSafeSetter } = useMountedState();

  // Create safe state setters to prevent updates on unmounted components
  const safeSetStatusLoading = createSafeSetter(setStatusLoading);

  // Early reCAPTCHA preloading effect for improved slow network performance
  useEffect(() => {
    if (isRecaptchaEnabled) {
      // Preload reCAPTCHA as early as possible, especially for slow networks
      preloadRecaptchaScript(networkSlow).catch((error) => {
        log.error('Failed to preload reCAPTCHA script:', error);
      });
    }
  }, [networkSlow, isRecaptchaEnabled]);

  // Effect to hide reCAPTCHA badge when disabled
  useEffect(() => {
    if (!isRecaptchaEnabled) {
      log.debug('Hiding reCAPTCHA badge (reCAPTCHA disabled)');
      hideBadge();
    }
  }, [isRecaptchaEnabled, hideBadge]);

  useEffect(() => {
    const statusRoutes = [
      ROUTE_CONSTANTS.STATUS.LOCKED,
      ROUTE_CONSTANTS.STATUS.UNLOCKED,
      ROUTE_CONSTANTS.STATUS.ELIGIBLE,
      ROUTE_CONSTANTS.STATUS.INELIGIBLE,
      ROUTE_CONSTANTS.STATUS.PENDING,
      ROUTE_CONSTANTS.STATUS.PHONE_NOT_ACTIVATED,
      ROUTE_CONSTANTS.STATUS.DELAYED,
    ];

    const prefetchRoutes = () => {
      if (router.prefetch === undefined) {
        return;
      }

      statusRoutes.forEach((statusRoute) => {
        router.prefetch(`${isSpanish ? '/es/device-unlock' : ''}/status/${statusRoute}`);
      });
    };

    if (globalThis.window !== undefined && 'requestIdleCallback' in globalThis.window) {
      const idleId = globalThis.window.requestIdleCallback(prefetchRoutes, { timeout: 2000 });
      return () => globalThis.window.cancelIdleCallback(idleId);
    }

    const timeoutId = setTimeout(prefetchRoutes, 500);
    return () => clearTimeout(timeoutId);
  }, [router, isSpanish]);

  // Loading state management methods
  const loadingStates = {
    isPageDataLoading: () => pageDataLoading,
    isStatusLoading: () => statusLoading,
    isAnyLoading: () => pageDataLoading || statusLoading,
    hasValidPageData: () => pageData?.body?.components && Array.isArray(pageData.body.components),
    shouldShowLoadingSpinner: () => pageDataLoading || statusLoading,
    getLoadingMessage: () => {
      if (pageDataLoading) return localeMessages.LOADING.LOADING_DATA;
      if (statusLoading) return localeMessages.LOADING.CHECKING_DEVICE;
      return '';
    },
    shouldShowSlowNetworkWarning: () => networkSlow && statusLoading
  };

  const prefetchStatusData = React.useCallback((statusRoute: StatusRoute, imeiValue: string) => {
    const trimmedImei = imeiValue?.trim();
    if (!trimmedImei) return;

    const cacheKey = `${statusRoute}-${trimmedImei}-${brand}`;

    if (getCachedData(cacheKey)) {
      return;
    }

    const existingRequest = getCachedRequest(cacheKey);
    if (existingRequest) {
      return;
    }

    const fetchStatus = STATUS_API_MAP[statusRoute];
    if (!fetchStatus) {
      return;
    }

    const requestPromise = fetchStatus(trimmedImei, brand)
      .then((data) => {
        setCachedData(cacheKey, data);
        clearRequestCache(cacheKey);
        return data;
      })
      .catch((prefetchError) => {
        clearRequestCache(cacheKey);
        log.warn(`Status prefetch failed for ${statusRoute}:`, prefetchError);
        throw prefetchError;
      });

    setCachedRequest(cacheKey, requestPromise);
  }, [brand]);

  // Extract IMEI validation logic
  const validateAndSetIMEI = (imei: string): boolean => {
    if (!imei) return false;

    const validation = validateIMEI(imei, isSpanish);
    if (!validation.valid) {
      setErrorBannerMessage(validation.error || localeMessages.ERRORS.INVALID_IMEI);
      return false;
    }
    return true;
  };

  // Extract eligibility check API call logic
  const checkDeviceEligibility = async (imei: string): Promise<EligibilityResponse> => {
    log.debug('Checking device eligibility for IMEI:', imei);
    
    let captchaToken: string;
    
    // Conditionally get reCAPTCHA token based on flag
    if (isRecaptchaEnabled) {
      try {
        // Try to get reCAPTCHA token with 5-second timeout
        log.debug('Attempting to get reCAPTCHA token...');
        const siteKey = await getRecaptchaSiteKey();
        if (!siteKey) {
          throw new RecaptchaError('reCAPTCHA site key not configured');
        }
        captchaToken = await window.grecaptcha.enterprise.execute(siteKey, { action: RECAPTCHA_CONSTANTS.ACTIONS.LOGIN });
        log.debug(`reCAPTCHA token received successfully (length: ${captchaToken.length})`);
        
      } catch (error) {
        log.warn(`Failed to get reCAPTCHA token:`, error instanceof Error ? error.message : String(error));
        throw new RecaptchaError('Failed to acquire reCAPTCHA token');
      }
    } else {
      // reCAPTCHA is disabled, use bypass token
      log.info('reCAPTCHA is disabled');
      throw new RecaptchaError('reCAPTCHA is disabled');
    }
    
    // Log token info (mask actual token for security)
    const tokenPreview = captchaToken === RECAPTCHA_CONSTANTS.FALLBACK.DEV_BYPASS_TOKEN 
      ? 'dev_bypass_token' 
      : `${captchaToken.substring(0, 10)}...${captchaToken.substring(captchaToken.length - 10)} (${captchaToken.length} chars)`;
    log.debug(`Using captcha token: ${tokenPreview}`);
    
    const response = await safeFetch('/device/device-unlock/api/check-eligibility', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        imei: imei.trim(),
        brand: brand,
        captchaToken: captchaToken
      })
    });

    return response.json();
  };

  // Extract device unlock status parsing logic
  const parseDeviceUnlockStatus = (eligibilityResponse: EligibilityResponse): ParsedDeviceUnlockStatus => {
    let deviceUnlockStatus = 'locked';
    let eligibleReason = '';
    let eligibleReasonCode = '';

    const serviceQualification = eligibilityResponse?.data?.serviceQualification;
    const qualificationResult = serviceQualification?.qualificationResult ?? 'NOT_ELIGIBLE';
    
    // Extract status from serviceEligibilityReasons
    if (serviceQualification?.serviceQualificationItems?.length && serviceQualification.serviceQualificationItems.length > 0) {
      const reasons: EligibilityReason[] = serviceQualification.serviceQualificationItems[0]?.serviceEligibilityReasons ?? [];
      
      const statusReason = reasons.find((r) => r.name === 'deviceUnlockStatus');
      if (statusReason?.value) {
        deviceUnlockStatus = statusReason.value.toUpperCase();
      }
      
      const reasonDetail = reasons.find((r) => r.name === 'eligibleReason');
      if (reasonDetail?.value) {
        eligibleReason = reasonDetail.value;
      }
      const reasonDetailCode = reasons.find((r) => r.name === 'eligibleReasonCode');
      if (reasonDetailCode?.value) {
        eligibleReasonCode = reasonDetailCode.value;
      }
    }
    
    return { deviceUnlockStatus, qualificationResult, eligibleReason, eligibleReasonCode };
  };

  // Extract route determination logic
  const determineStatusRoute = (deviceUnlockStatus: string, qualificationResult: string, eligibleReason: string, eligibleReasonCode?: string): StatusRoute => {
    log.debug('Device Unlock Status:', deviceUnlockStatus);
    log.debug('Qualification Result:', qualificationResult);
    log.debug('Eligible Reason:', eligibleReason);
    log.debug('Eligible Reason Code:', eligibleReasonCode);
    
    // Priority: UNLOCKED > NOT ELIGIBLE > ELIGIBLE > LOCKED
    if (deviceUnlockStatus === 'UNLOCKED') {
      return ROUTE_CONSTANTS.STATUS.UNLOCKED;
    }
    
    if (qualificationResult === 'NOT ELIGIBLE') {
      return ROUTE_CONSTANTS.STATUS.INELIGIBLE;
    }
    
    if (qualificationResult === 'ELIGIBLE' && deviceUnlockStatus === 'LOCKED' && (eligibleReasonCode === '30230' || eligibleReasonCode === '10621')) {
      return ROUTE_CONSTANTS.STATUS.PENDING;
    }
    
    if (qualificationResult === 'ELIGIBLE' && deviceUnlockStatus === 'UNLOCKING' && eligibleReasonCode === '30220') {
      return ROUTE_CONSTANTS.STATUS.PENDING;
    }

    if (qualificationResult === 'ELIGIBLE' && deviceUnlockStatus === 'LOCKED') {
      return ROUTE_CONSTANTS.STATUS.ELIGIBLE;
    }
    
    return ROUTE_CONSTANTS.STATUS.LOCKED;
  };

  // Extract IMEI form submission logic
  const processIMEISubmission = async (imei: string): Promise<void> => {
    if (!validateAndSetIMEI(imei)) return;

    setErrorBannerMessage(null);
    safeSetStatusLoading(true);

    try {
      const eligibilityResponse = await checkDeviceEligibility(imei);
      log.debug('Eligibility response:', eligibilityResponse);
      
    const { deviceUnlockStatus, qualificationResult, eligibleReason, eligibleReasonCode } = parseDeviceUnlockStatus(eligibilityResponse);
    const statusRoute = determineStatusRoute(deviceUnlockStatus, qualificationResult, eligibleReason, eligibleReasonCode);
    prefetchStatusData(statusRoute, imei);
    // Store the raw eligibleReasonCode so the status page can resolve the
    // locale-specific display text from the current locale's AEM data.
    // Storing the code (not the resolved text) ensures language switches
    // always render the correctly translated message.
    // Store IMEI in context and navigate immediately while loader is still showing
      setIMEI(imei);
      setEligibleReason(eligibleReasonCode || '');
      router.push(`${isSpanish ? '/es/device-unlock' : ''}/status/${statusRoute}`);
      // Don't stop loading here - let it continue during navigation for smoother transition
    } catch (apiError) {
      log.error('API call failed for device eligibility check:', apiError);
      
      // Don't route to next page on API failure - show error to user instead
      let errorMessage: string = localeMessages.ERRORS.API_ERROR;
      
      // Provide more specific error messages based on error type
      if (apiError instanceof Error) {
        if (apiError.message.includes('fetch')) {
          errorMessage = localeMessages.ERRORS.NETWORK_TIMEOUT;
        } else if (apiError.message.includes('timeout')) {
          errorMessage = localeMessages.ERRORS.NETWORK_TIMEOUT;
        } else if (apiError.message.includes('500') || apiError.message.includes('502') || apiError.message.includes('503')) {
          errorMessage = localeMessages.ERRORS.SERVER_UNAVAILABLE;
        } else if (apiError.message.includes('400') || apiError.message.includes('401') || apiError.message.includes('403')) {
          errorMessage = localeMessages.ERRORS.INVALID_REQUEST;
        }
      }
      
      setErrorBannerMessage(errorMessage);
      // Stop loading only on error
      safeSetStatusLoading(false);
    }
  };

  // Handle form submission from ComponentRenderer (IMEI check form)
  const handleFormSubmit = async (formData: Record<string, unknown>) => {
    log.debug('Form submitted with data:', formData);
    
    // Check if this is IMEI field from home page
    const imei = formData[FORM_CONSTANTS.FIELDS.IMEI] as string;
    if (!imei) return;

    // Process IMEI submission - all error handling is done within processIMEISubmission
    await processIMEISubmission(imei);
  };

  // Handle navigation from ComponentRenderer
  const handleNavigation = (destination: string) => {
    log.debug('Navigate to:', destination);
  };

  // Render loading state if any loading is in progress
  if (loadingStates.shouldShowLoadingSpinner()) {
    return (
      <div className="min-h-[100dvh] bg-white text-gray-800 flex flex-col overflow-x-hidden" style={{ fontFamily: STYLE_CONSTANTS.FONTS.PRIMARY }}>
        <main id="main-content" className="flex-1 flex items-center justify-center px-4" aria-label="Device unlock content">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
            <p className="text-gray-600">
              {loadingStates.getLoadingMessage()}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-white text-gray-800 flex flex-col overflow-x-hidden" style={{ fontFamily: STYLE_CONSTANTS.FONTS.PRIMARY }}>

      <main id="main-content" className="flex-1 w-full" aria-label="Device unlock content">
        {errorBannerMessage && (
          <div className="w-full px-4 sm:px-6 lg:px-8 mt-12 md:mt-4">
            <ErrorBanner
              message={errorBannerMessage}
              onClose={() => setErrorBannerMessage(null)}
              containerClassName="max-w-4xl mx-auto mb-4"
              bannerClassName="border border-red-200 bg-red-50 text-red-700 rounded-md px-4 py-3 text-left flex items-start gap-3"
            />
          </div>
        )}

        {loadingStates.hasValidPageData() && pageData?.body?.components ? (
          pageData.body.components.map((component, index) => (
            <ComponentRenderer 
              key={component.type || index}
              component={component}
              onSubmit={handleFormSubmit}
              onNavigate={handleNavigation}
              isRecaptchaReady={isRecaptchaReady}
              isRecaptchaEnabled={isRecaptchaEnabled}
              isSlowNetwork={networkSlow}
            />
          ))
        ) : (
          <div className="min-h-[100dvh] bg-white flex items-center justify-center px-4">
            <div className="text-center">
              <p className="text-red-600 mb-4">Error loading page content</p>
              <p className="text-gray-600">Invalid API response structure</p>
              <button 
                onClick={() => globalThis.window.location.reload()} 
                className="mt-4 px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
              >
                Retry
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default UnlockPortalHomePage;